import { FilterConfig } from 'SRC/fetch/api/observability';

let counter = 0;

const GENERATED_PREFIX = 'generated-';
const QUICK_FILTER_KEY = 'quick';
const FILTER_KEY_SEPARATOR = '.';

// Helper function to extract filter key parts
function getFilterKeyParts(filter: Partial<FilterConfig> | string): string[] {
  const filterKey = typeof filter === 'string' ? filter : filter?.key;
  return filterKey ? filterKey.split('.') : [];
}

export function isNestedFilterConfig(
  filterConfig: Partial<FilterConfig>,
): boolean {
  if (!filterConfig) {
    return false;
  }
  return !!filterConfig.cubejsKeys && filterConfig.cubejsKeys.length > 1;
}

export function isNestedFilterKey(
  filter: Partial<FilterConfig> | string | undefined,
): filter is string {
  if (!filter) {
    return false;
  }
  return getFilterKeyParts(filter).length > 1;
}

export function getNestedKey(filter: Partial<FilterConfig> | string): string {
  const filterKeyParts = getFilterKeyParts(filter);
  if (filterKeyParts.length === 0) {
    throw new Error('Invalid filter key');
  }
  return filterKeyParts[0];
}

export function getNestedKeyValue(
  filter: Partial<FilterConfig> | string,
): string | undefined {
  const filterKeyParts = getFilterKeyParts(filter);
  if (filterKeyParts.length === 0) {
    throw new Error('Invalid filter key');
  }
  return filterKeyParts[1];
}

export function splitUrlParamKey(key: string): {
  filterKey: string;
  urlOperator: string;
} {
  const lastDotIndex = key.lastIndexOf('.');
  const filterKey = key.substring(0, lastDotIndex);
  const urlOperator = key.substring(lastDotIndex + 1);
  return {
    filterKey,
    urlOperator,
  };
}

export function generateFilterKey(filter: FilterConfig) {
  if (!isNestedFilterConfig(filter)) {
    return filter.key;
  }
  const timestamp = Date.now();
  // Increment the counter
  counter += 1;

  // Create a field ID using both the timestamp and the counter to ensure uniqueness
  const field = `${GENERATED_PREFIX}${timestamp}-${counter}`;

  return `${filter.key}${FILTER_KEY_SEPARATOR}${field}`;
}

export function isGeneratedFilterKey(key: string) {
  return key.includes(GENERATED_PREFIX);
}

export function isNestedQuickFilterKey(key: string) {
  return key.includes(QUICK_FILTER_KEY);
}

export function generateNestedQuickFilterKey(
  filter: FilterConfig,
  nestedValue?: string,
) {
  if (!isNestedFilterConfig(filter)) {
    return filter.key;
  }
  return `${filter.key}${FILTER_KEY_SEPARATOR}${QUICK_FILTER_KEY}${
    nestedValue ? `${FILTER_KEY_SEPARATOR}${nestedValue}` : ''
  }`;
}

export function getUpdatedNestedFormKey(
  key: string,
  nestedValue?: string,
): string {
  const nestedKey = getNestedKey(key);
  return `${nestedKey}${FILTER_KEY_SEPARATOR}${nestedValue}`;
}

export function getTrueNestedKey(key: string) {
  if (isNestedQuickFilterKey(key)) {
    return key
      .split(FILTER_KEY_SEPARATOR)
      .filter((element) => element !== QUICK_FILTER_KEY)
      .join(FILTER_KEY_SEPARATOR);
  } else {
    return key;
  }
}

export function getNestedKeyValues(key: string) {
  if (isGeneratedFilterKey(key)) {
    return undefined;
  }
  if (isNestedQuickFilterKey(key)) {
    const value = key.split(FILTER_KEY_SEPARATOR).pop();
    return value === QUICK_FILTER_KEY ? undefined : value;
  }
  // Remove the filter key and the dot and return the rest of the string
  return key.split(FILTER_KEY_SEPARATOR).pop();
}
